import { Component, OnInit, Input } from '@angular/core';
import { IntegrationHistoryItem, IntegrationHistoryItemTypeLabel, IntegrationFieldTypeLabel } from '@app-model/evaluation';
import { EvaluationService } from '@app-services/evaluation.service';
import { Permission, Role, UserInfo } from '@app/store/session/session.store';
import { IntegrationProviderLabel } from '@app/views/private/admin/model/advertisers';
import { ToastrService } from 'ngx-toastr';
import { of, timer } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { EvaluationVehicle } from '@app-model/evaluation';

@Component({
  selector: 'app-integration-history',
  templateUrl: './integration-history.component.html',
  styleUrls: ['./integration-history.component.scss']
})
export class IntegrationHistoryComponent implements OnInit {
  @Input() items: IntegrationHistoryItem[];
  @Input() numHistoryItems: number;
  @Input() vehicleId: string;
  @Input() historyType: string;
  @Input() userInfo: UserInfo;
  @Input() vehicle: EvaluationVehicle;

  showMore = true;
  isResynchronizing = false;

  constructor(
    private evaluationService: EvaluationService,
    private toastr: ToastrService,
  ) { }

  ngOnInit() {
    this.showMore = (this.numHistoryItems > 0 && this.numHistoryItems > this.items.length);
  }

  onClickFullHistory() {
    if (this.historyType !== 'EVAL') {
      return;
    }

    this.evaluationService.fullIntegrationHistory(this.vehicleId).subscribe({
      next: (response) => {
        this.showMore = false;
        this.items = response;
      },
      error: () => this.handleFullIntegrationHistoryError(),
    });
  }

  onClickResynchronize(): void {
    if (this.isResynchronizing || !this.vehicleId) {
      return;
    }

    this.isResynchronizing = true;
    this.evaluationService.resynchronizeEval(this.vehicleId).pipe(
      switchMap(() => timer(5000)),
      switchMap(() => this.evaluationService.fullIntegrationHistory(this.vehicleId).pipe(
        catchError(() => {
          this.handleFullIntegrationHistoryError();
          return of([]);
        }),
      )),
      tap((response) => {
        if (!response?.length) {
          return;
        }
        this.showMore = false;
        this.items = response;
      }),
      finalize(() => {
        this.isResynchronizing = false;
      }),
    ).subscribe();
  }

  getItemTypeLabel(item: IntegrationHistoryItem): string {
    return IntegrationHistoryItemTypeLabel[item.type];
  }

  getItemProviderLabel(item: IntegrationHistoryItem): string {
    return IntegrationProviderLabel.getLabel(item.provider);
  }

  getItemFieldsLabels(item: IntegrationHistoryItem): string {

    if (item.fieldTypes && item.fieldTypes.length > 0) {
      const fields = [];
      item.fieldTypes.forEach(f => fields.push(IntegrationFieldTypeLabel[f]));

      return fields.join(', ');
    } else {
      return undefined;
    }
  }

  hasSensitiveDetails(item: IntegrationHistoryItem): boolean {
    return !!(item.method || item.integrationUrl || item.requestBody || item.response);
  }

  showDetailsLink(item: IntegrationHistoryItem): boolean {
    return this.canViewIntegrationHistoryDetails() && this.hasSensitiveDetails(item);
  }

  openDetailsLink(item: IntegrationHistoryItem): void {
    if (!this.showDetailsLink(item)) {
      return;
    }
    item.showingDetails = true;
  }

  closeDetailsLink(item: IntegrationHistoryItem): void {
    item.showingDetails = false;
  }

  canResynchronize(): boolean {
    const hasLegacyRoles = this.userInfo?.hasRoles(Role.ADMIN, Role.OEM_DEALER_MANAGER, Role.EVAL_DEALER_MANAGER, Role.SELLER);
    const hasPermission = this.userInfo?.hasAnyPermission(Permission.RESYNC_EVALUATION_INTEGRATION);
    return hasLegacyRoles || hasPermission;
  }

  canViewIntegrationHistoryDetails(): boolean {
    return this.userInfo?.canViewIntegrationHistoryDetails() ?? false;
  }

  private handleFullIntegrationHistoryError(): void {
    this.toastr.error('Não foi possível carregar o histórico de integração.');
  }

}
